import { useEffect, useState } from "react";
import { MapPin, Clock, Phone, Mail, Car, Train } from "lucide-react";
import { Link } from "react-router-dom";
import HeroSlideshow from "@/components/HeroSlideshow";
import { useSwipeToNavigate } from "@/components/MobilePageDots";
import { listSiteContent, absoluteUrl } from "@/lib/api";

const DEFAULT_HERO = {
  overline: "Find Us",
  title_top: "Senopati,",
  title_italic: "South Jakarta.",
  body:
    "Tucked along Jalan Suryo — a quiet corner of the neighbourhood, lit by lanterns after dusk.",
};

const DEFAULT_INFO = {
  hours: [
    { day: "Monday", time: "Closed" },
    { day: "Tuesday – Thursday", time: "18:00 – 23:00" },
    { day: "Friday – Saturday", time: "18:00 – 00:00" },
    { day: "Sunday", time: "17:00 – 22:00" },
  ],
  by_car:
    "Valet parking is offered nightly from 17:30. Complimentary for dining guests.",
  by_mrt:
    "8-minute walk from ASEAN Station (M1 line), exit B toward Senopati.",
  closing_quote: "See you at the table",
  map_query: "Jalan Suryo, Senopati, Kebayoran Baru, South Jakarta",
};

const DEFAULT_CONTACT = {
  phone: "+62 21 1234 5678",
  email_general: "hello@nelumdining.com",
  address_line1: "Jl. Suryo No. 24",
  address_line2: "Senopati, Kebayoran Baru",
};

export default function Location() {
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [info, setInfo] = useState(DEFAULT_INFO);
  const [contact, setContact] = useState(DEFAULT_CONTACT);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listSiteContent();
        if (cancelled) return;
        const h = items.find((c) => c.section === "location-hero");
        const i = items.find((c) => c.section === "location-info");
        const ci = items.find((c) => c.section === "contact-info");
        if (h) setHero({ ...DEFAULT_HERO, ...h });
        if (i) setInfo({ ...DEFAULT_INFO, ...i, hours: i.hours?.length ? i.hours : DEFAULT_INFO.hours });
        if (ci) setContact({ ...DEFAULT_CONTACT, ...ci });
      } catch (err) {
        console.warn("[Location] Failed to load site content — using defaults", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const telHref = contact.phone ? `tel:${contact.phone.replace(/[^+\d]/g, "")}` : "#";
  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(
    info.map_query || DEFAULT_INFO.map_query,
  )}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

  return (
    <div data-testid="page-location" {...useSwipeToNavigate()} className="touch-pan-y">
      {/* Header */}
      <section className="relative border-b border-border min-h-[70vh] flex items-center">
        <HeroSlideshow page="location" />
        <div className="relative z-10 w-full mx-auto max-w-[1400px] px-6 lg:px-12 py-32 lg:py-40 fade-up">
          <div className="overline">{hero.overline || "Find Us"}</div>
          {hero.title_visible !== false && (
            <h1
              data-testid="location-hero-title"
              className="mt-8 serif text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tighter text-foreground [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]"
            >
              {hero.title_top || "Senopati,"} <br />
              <span className="italic text-foreground">{hero.title_italic || "South Jakarta."}</span>
            </h1>
          )}
          {hero.body_visible !== false && hero.body && (
            <p
              data-testid="location-hero-body"
              className="mt-8 max-w-xl text-sm md:text-base leading-relaxed text-foreground/90 [text-shadow:0_1px_10px_rgba(0,0,0,0.5)] whitespace-pre-line"
            >
              {hero.body}
            </p>
          )}
        </div>
      </section>

      {/* Address + hours grid */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 space-y-14">
            <div>
              <div className="overline flex items-center gap-2">
                <MapPin size={12} strokeWidth={1.5} /> Address
              </div>
              <div className="mt-6 serif text-3xl md:text-4xl font-light leading-tight text-foreground">
                {contact.address_line1 || DEFAULT_CONTACT.address_line1}
              </div>
              <div className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {contact.address_line2 || DEFAULT_CONTACT.address_line2}
              </div>
            </div>

            <div>
              <div className="overline flex items-center gap-2">
                <Clock size={12} strokeWidth={1.5} /> Hours
              </div>
              <div className="mt-6 space-y-3">
                {(info.hours || []).map((h, idx) => (
                  <div
                    key={`${h.day}-${idx}`}
                    data-testid={`hours-${(h.day || "").toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-baseline justify-between border-b border-border pb-3 text-sm"
                  >
                    <span className="text-foreground/85">{h.day}</span>
                    <span
                      className={
                        (h.time || "").toLowerCase() === "closed"
                          ? "text-muted-foreground italic"
                          : "text-primary tabular-nums"
                      }
                    >
                      {h.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {contact.phone && (
                <div>
                  <div className="overline flex items-center gap-2">
                    <Phone size={12} strokeWidth={1.5} /> Reservations
                  </div>
                  <a
                    href={telHref}
                    data-testid="location-phone"
                    className="mt-4 block serif text-xl text-foreground hover:text-primary transition-colors"
                  >
                    {contact.phone}
                  </a>
                </div>
              )}
              {contact.email_general && (
                <div>
                  <div className="overline flex items-center gap-2">
                    <Mail size={12} strokeWidth={1.5} /> Enquiries
                  </div>
                  <a
                    href={`mailto:${contact.email_general}`}
                    data-testid="location-email"
                    className="mt-4 block serif text-xl text-foreground hover:text-primary transition-colors"
                  >
                    {contact.email_general}
                  </a>
                </div>
              )}
            </div>

            <div className="pt-4">
              <Link
                to="/reservation"
                data-testid="location-cta-reserve"
                className="inline-flex items-center gap-3 px-7 py-4 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.24em] font-semibold hover:bg-primary/90 transition-colors"
              >
                Reserve a Table
              </Link>
            </div>
          </div>

          {/* Map + arrival */}
          <div className="lg:col-span-7 space-y-8">
            <div
              data-testid="location-map"
              className="aspect-[4/3] w-full border border-border overflow-hidden bg-card"
            >
              <iframe
                title="Nelum Dining Map"
                src={mapSrc}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.6) invert(0.92) hue-rotate(180deg) contrast(0.9)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              {info.by_car && (
                <div className="border border-border p-6 bg-card">
                  <div className="flex items-center gap-2 overline">
                    <Car size={12} strokeWidth={1.5} /> By Car
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                    {info.by_car}
                  </p>
                </div>
              )}
              {info.by_mrt && (
                <div className="border border-border p-6 bg-card">
                  <div className="flex items-center gap-2 overline">
                    <Train size={12} strokeWidth={1.5} /> By MRT
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                    {info.by_mrt}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom image / video / quote section */}
      {(info.closing_quote || info.image_primary || info.video_primary) && (
        <section className="relative h-[60vh] overflow-hidden" data-testid="location-closing">
          {info.video_primary ? (
            <video
              src={absoluteUrl(info.video_primary)}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
              data-testid="location-closing-video"
            />
          ) : info.image_primary ? (
            <img
              src={absoluteUrl(info.image_primary)}
              alt="Nelum"
              className="w-full h-full object-cover"
              data-testid="location-closing-image"
            />
          ) : (
            <div className="w-full h-full bg-card/40" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
          {info.closing_quote && (
            <div className="absolute inset-x-0 bottom-0 mx-auto max-w-[1400px] px-6 lg:px-12 pb-16">
              <div
                className="serif italic text-2xl md:text-3xl text-foreground/90 max-w-xl"
                data-testid="location-closing-quote"
              >
                <span className="ornament">{info.closing_quote}</span>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
